import { generateCertificationError, updateCertificationCompletedById, updateCertificationStatusById } from "api/certifications/certifications";
import { fetchColleges } from "api/colleges/colleges";
import { fetchUsers } from "api/users/users";
import moment from "moment";
import { certificationActions } from "store/slices/certification/certificationSilce";
import { handleApiError } from "utils";

/**
 * Hook personalizado que encapsula la lógica para reportar errores en una certificación EPST
 * incluyendo la actualización del estado de la certificación, la obtención del usuario y colegio,
 * y la generación del reporte de error.
 *
 * @param {Object} params - Parámetros requeridos para el hook.
 * @param {Object} params.certification - Objeto de la certificación actual.
 * @param {string} params.token - Token de autenticación del usuario.
 * @param {Function} params.dispatch - Función dispatch de Redux para disparar acciones.
 * @param {Function} params.navigate - Función de navegación (useNavigate de React Router).
 * @param {string} params.username - Username del usuario autenticado.
 *
 * @returns {{ handleUpdateCertificationStatusById: Function }} - Función para iniciar el flujo de reporte de error.
 */
const useCertificationErrorReport = ({ certification, token, dispatch, navigate, username }) => {

    /**
     * Actualiza el estado de la certificación en la base de datos.
     * Inicia el flujo para obtener los datos del usuario y generar el reporte de error.
     *
     * @param {Object} data - Objeto con el nuevo estado de la certificación, por ejemplo `{ status: 4 }`.
     */
	const handleUpdateCertificationStatusById = async (data) => {
		try {
			await updateCertificationStatusById(certification.id, data, token);
			handleUserByUsername();
		} catch (error) {
			handleApiError(error);
		}
	};

    /**
     * Obtiene los datos del usuario actual mediante su correo electrónico.
     * Si tiene éxito, continúa con la obtención de los datos del colegio.
     */
	const handleUserByUsername = async () => {
		try {
			const { data } = await fetchUsers({ username: encodeURIComponent(username) }, token);
			handleCollegeById(data);
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Obtiene los datos del colegio al que pertenece el usuario.
	 * Si tiene éxito, actualiza la fecha de finalización de la certificación.
	 *
	 * @param {Object} user - Objeto con los datos del usuario.
	 */
	const handleCollegeById = async (user) => {
		try {
			const { data } = await fetchColleges({ id: certification.college_id }, token);
			handleUpdateCertificationCompletedById(user, data);
		} catch (error) {
			handleApiError(error);
		}
	};

    /**
     * Registra la fecha de finalización de la certificación y continúa con la generación del error.
     *
     * @param {Object} user - Objeto con los datos del usuario.
     * @param {Object} college - Objeto con los datos del colegio.
     */
	const handleUpdateCertificationCompletedById = async (user, college) => {
		try {
			const date = moment().tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');
			await updateCertificationCompletedById(certification.id, { completed_date: date }, token);

			const payload = {
				name: `${user.name} ${user.lastname}`,
				college: college.name,
				email: user.email,
				certification: 'EPSt',
				date
			};
			handleGenerateCertificationError(payload);
		} catch (error) {
			handleApiError(error);
		}
	};

    /**
     * Genera un reporte de error de certificación.
     * Si tiene éxito, resetea la certificación en Redux y redirige al usuario al home.
     *
     * @param {Object} payload - Información del error a reportar.
     */
	const handleGenerateCertificationError = async (payload) => {
		try {
			const { data } = await generateCertificationError(payload, token);
			if (data?.status === 200) {
				dispatch(certificationActions.resetCertification());
				navigate('/');
			}
		} catch (error) {
			handleApiError(error);
		}
	};

	return {
		handleUpdateCertificationStatusById
	};
};

export default useCertificationErrorReport;